import queryString from 'query-string';
import dayjs from 'dayjs';
import { tryFetch, getAssetLink } from '../helpers/api';
import { initMultipleSelect } from '../global-modules';
import I18n from '../helpers/i18n';
import { translations } from '../translations/translations';

export class StoriesList {
  constructor(target) {
    this.section = target;

    this.form = this.section.querySelector('#filters');
    this.brandSelect = this.section.querySelector('[name="brand"]');
    this.storyTypeSelect = this.section.querySelector('[name="storyType"]');
    this.countrySelect = this.section.querySelector('[name="country"]');

    this.selectedValues = this.section.querySelector('[data-selected-values]');
    this.totalLabel = this.section.querySelector('[data-total]');

    this.mainGrid = this.section.querySelector('.main-grid [data-list]');
    this.loadMoreBtn = this.section.querySelector('[data-load-more]');

    this.detailsUrl = this.section?.dataset?.detailsUrl || '';

    this.storiesListPath = window.location.pathname.replace('.html', '').replace(CMS.contextPath, '');

    this.filters = {
      brand: [],
      storyType: [],
      country: [],
      limit: 6,
      page: 1,
      orderBy: 'publicationDate desc',
      lang: document.documentElement.lang || 'en',
    };

    this.response = null;
    this.accumulator = [];
    this.i18n = new I18n(translations.storiesList);
  }

  init () {
    this.loading(false);
    this.getSelectedFilters();
    this.onFiltersChipClick();
    this.onFiltersReset();
    this.onFiltersChange();
    // this.afterFiltersChange();
  }

  loading(isLoading = true) {
    this.section.classList.toggle('loading', isLoading);
  }

  getSelectedFilters() {
    if (this.brandSelect.length) {
      this.filters.brand = this.getMultiSelectValue(this.brandSelect);
    }

    if (this.storyTypeSelect.length) {
      this.filters.storyType = this.getMultiSelectValue(this.storyTypeSelect);
    }

    if (this.countrySelect.length) {
      this.filters.country = this.getMultiSelectValue(this.countrySelect);
    }
  }

  onFiltersChipClick () {
    this.selectedValues.addEventListener('click', (e) => {
      const itemValue = e.target.closest('button').dataset.removeItem;

      if (this.brandSelect.querySelector(`[value="${itemValue}"]`)) {
        this.brandSelect._multipleSelect.uncheck(itemValue);
        this.brandSelect.dispatchEvent(new Event('change', {
          bubbles: true
        }));
      }

      if (this.storyTypeSelect.querySelector(`[value="${itemValue}"]`)) {
        this.storyTypeSelect._multipleSelect.uncheck(itemValue);
        this.storyTypeSelect.dispatchEvent(new Event('change', {
          bubbles: true
        }));
      }

      if (this.countrySelect.querySelector(`[value="${itemValue}"]`)) {
        this.countrySelect._multipleSelect.uncheck(itemValue);
        this.countrySelect.dispatchEvent(new Event('change', {
          bubbles: true
        }));
      }
    });
  }

  onFiltersReset () {
    this.form.addEventListener('reset', () => {
      this.brandSelect._multipleSelect.uncheckAll();
      this.storyTypeSelect._multipleSelect.uncheckAll();
      this.countrySelect._multipleSelect.uncheckAll();

      this.filters.brand = [];
      this.filters.country = [];
      this.filters.storyType = [];

      this.brandSelect.dispatchEvent(new Event('change', {
        bubbles: true
      }));
      this.storyTypeSelect.dispatchEvent(new Event('change', {
        bubbles: true
      }));
      this.countrySelect.dispatchEvent(new Event('change', {
        bubbles: true
      }));
    });
  }

  onFiltersChange () {
    this.form.addEventListener('change', async (e) => {
      let currentSelect;

      if (e.target.type === 'checkbox') {
        currentSelect = e.target.closest('.ms-parent').previousSibling;
      }

      if (e.target.type === 'select-multiple') {
        currentSelect = e.target;
      }

      this.filters[currentSelect?.name] = this.getMultiSelectValue(currentSelect);
      this.filters.page = 1;

      await this.afterFiltersChange();
    });

    this.loadMoreBtn.addEventListener('click', async (e) => {
      e.preventDefault();

      this.filters.page += 1;
      await this.afterFiltersChange();
    });
  }

  async afterFiltersChange () {
    this.pushToHistory();
    this.toggleFiltersSummary();
    await this.getData();
    this.prepareHTMLItems();
    this.renderSelectedValues();

    if (this.filters.page === 1 && this.response?.results?.length) {
      this.renderFirstPage();
    }

    if (this.filters.page === 1 && !this.response?.results?.length) {
      this.renderNoResults();
    }

    if (this.filters.page > 1 && this.response?.results?.length) {
      this.renderConsequentPage();
    }

    this.handlePagination();
    this.renderFilters();
  }

  renderFilters() {
    this.form.innerHTML = '';

    const filtersHTML = this.response.filters.map((filter) => `
      <label>
        <select
          multiple="multiple"
          class="multiple-select"
          placeholder="${this.i18n.get(filter.name).toUpperCase()}"
          name="${filter.name}"
        >
          ${filter?.values?.map((option) => `
            <option value="${option?.value}" ${option?.selected ? 'selected' : ''}>
              ${filter.name === 'storyType' ?
                this.i18n.get(option?.value?.toLowerCase()) : (option?.displayValue || option?.value || '')}
            </option>
          `).join('')}
        </select>
      </label>
    `);

    this.form.innerHTML = filtersHTML.join('');

    this.brandSelect = this.section.querySelector('[name="brand"]');
    this.storyTypeSelect = this.section.querySelector('[name="storyType"]');
    this.countrySelect = this.section.querySelector('[name="country"]');

    initMultipleSelect(this.form);
  }

  renderSelectedValues () {
    this.totalLabel.innerHTML = `${this.response.total} ${this.i18n.get('results')}`;

    const HTMLSelectedFilters = this.response.filters.map((filter) => {
      const isAnyValueSelected = filter?.values?.some((value) => value.selected);
      let HTMLFilterValues = [];

      if (isAnyValueSelected) {
        HTMLFilterValues = filter.values.map((filterValue) =>
          (filterValue?.selected
            ? `
          <li>
            <span class="chip deletable">
              ${filter.name === 'storyType' ?
                this.i18n.get(filterValue?.value?.toLowerCase()) :
                (filterValue?.displayValue || filterValue?.value || '')}
              <button type="button" data-remove-item="${filterValue?.value}" data-remove-from-filter=${filter?.name}>
                <svg width="10" height="10" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 1L1 19" stroke="black" stroke-width="3" stroke-linejoin="round"/>
                  <path d="M1 1L19 19" stroke="black" stroke-width="3" stroke-linejoin="round"/>
                </svg>
              </button>
            </span>
          </li>
        `
            : '')
        );
      }

      return isAnyValueSelected
        ? `
        <div class="chip-group">
          <span class="group-title">${this.i18n.get(filter.name)}</span>
          <ul class="chip-list">
            ${HTMLFilterValues.join('')}
          </ul>
        </div>
      `
        : '';
    });

    const HTMLSelectedFiltersFinished = HTMLSelectedFilters.join('');

    this.selectedValues.innerHTML = '';
    this.selectedValues.innerHTML = HTMLSelectedFiltersFinished;
  }

  toggleFiltersSummary () {
    const filtersActive = Boolean(
      this.filters.country.length || this.filters.storyType.length || this.filters.brand.length
    );

    this.section.classList.toggle('filters-active', filtersActive);
  }

  handlePagination () {
    const isLastPage = this.response.offset + this.response.limit >= this.response.total;

    this.loadMoreBtn.classList.toggle('hide', isLastPage);
  }

  renderNoResults () {
    this.mainGrid.innerHTML = '';
    this.mainGrid.innerHTML = `
      <div class="small-12 columns">
        ${this.i18n.get('noResults')}
      </div>
    `;
  }

  renderFirstPage () {
    this.mainGrid.innerHTML = '';
    this.mainGrid.innerHTML = this.HTMLItems.join('');
  }

  renderConsequentPage () {
    this.mainGrid.insertAdjacentHTML('beforeend', this.HTMLItems.join(''));
  }

  pushToHistory () {
    const queryParams = {
      'filters[country]': this.filters.country,
      'filters[brand]': this.filters.brand,
      'filters[storyType]': this.filters.storyType,
      page: this.filters.page,
    };

    const qs = queryString.stringify(queryParams, {
      arrayFormat: 'separator',
      arrayFormatSeparator: ','
    });

    window.history.pushState(this.filters, '', encodeURI(`?${qs}`));
  }

  getMultiSelectValue (target) {
    return target?._multipleSelect?.data?.filter(el => el.selected)?.map(el => el.value) || [];
  }

  async getData () {
    const APIQueryParams = { ...this.filters };

    APIQueryParams['filters[brand]'] = this.filters.brand;
    APIQueryParams['filters[storyType]'] = this.filters.storyType;
    APIQueryParams['filters[country]'] = this.filters.country;
    APIQueryParams.offset = this.pageToOffset(this.filters.page);
    delete APIQueryParams.brand;
    delete APIQueryParams.country;
    delete APIQueryParams.storyType;
    delete APIQueryParams.page;

    const params = queryString.stringify(APIQueryParams, {
      arrayFormat: 'none'
    });

    const API_URL = `${CMS.contextPath}/.rest/delivery/stories?${params}`;
    const [response] = await tryFetch(API_URL);

    this.response = response;
  }

  prepareHTMLItems () {
    if (!this.response?.results?.length) {
      return;
    }

    this.HTMLItems = this.response.results.map((story) => {
      const _story = {
        ...story,
        img: getAssetLink(story.image),
        imgAlt: story?.image?.alt || '',
        url: `${CMS.contextPath}${story['@path']}`,
        country: story?.country?.displayName || story?.country?.['@name']
      };

      return this.getStoryCardTemplate(_story);
    });
  }

  getStoryCardTemplate(story) {
    const {
      img = '',
      imgAlt = '',
      storyType = '',
      country = '',
      brand = '',
      url = '',
      title = ''
    } = story;

    const SubtitleFragment = this.getSubtitleFragment(story);
    const DateRowFragment = this.getDateRowFragment(story);

    return `
        <div class="small-12 medium-6 large-4 columns margin-bottom-2">
          <div class="story-card">
            <div class="image-wrapper">
              <img src="${img}" alt="${imgAlt}">
            </div>

            <div class="info-row">
              <span class="tags-wrapper">
                <span class="tag primary">${storyType}</span>
                <span class="tag bordered">${country}</span>
              </span>
              <span class="brand ${brand?.toLowerCase()}">${brand}</span>
            </div>

            <a href="${url}" class="related-title">${title}</a>

            ${storyType === 'NEWS' ? SubtitleFragment : ''}

            ${storyType === 'FAIR' || storyType === 'COMPETITION' ? DateRowFragment : ''}
          </div>
        </div>
      `;
  }

  getSubtitleFragment(story = '') {
    const { subtitle = '' } = story;

    return subtitle ? `<p class="related-subtitle">${subtitle}</p>` : '';
  }

  getDateRowFragment(story) {
    const { dateFrom = '', dateTo = '', location = '' } = story;

    return `
        <div class="date-row">
          <span>
            ${dateFrom ? dayjs(dateFrom).format('DD MMM') : ''}
            ${dateTo ? '-' : ''}
            ${dateTo ? dayjs(dateTo).format('DD MMM YYYY') : ''}
          </span>
          <span class="margin-left-1">${location}</span>
        </div>
      `;
  }

  pageToOffset (pageNumber = 1, pageSize = 6) {
    return (pageNumber - 1) * pageSize;
  }
}

